<template>
  <BaseSection>
    <template #header>
      <span class="font-bold"> Gateway: {{ gatewaySerial }} </span>
    </template>

    <div class="flex items-center mb-3">
      <DmgwReadoutDebug
        :readout-state="readoutState"
        :device-readout-state="deviceReadoutState"
      />
      <button
        class="ml-auto text-xs text-red-500 hover:text-red-600"
        @click="$emit('cancel')"
      >
        Abbrechen
      </button>
    </div>

    <div
      v-if="
        readoutState.status === 'reading' ||
        readoutState.status === 'done' ||
        hasPreviousResults
      "
    >
      <div class="flex items-center gap-3 mb-2 flex-wrap">
        <div
          v-if="readoutState.status === 'done'"
          class="flex items-center gap-1.5 text-sm text-green-600"
        >
          <em class="mdi mdi-check-circle" />
          <span class="font-medium">Fertig</span>
        </div>

        <div
          v-if="mergedDevices.length > 0"
          class="inline-flex rounded-lg bg-light-2 dark:bg-dark-2 p-0.5"
        >
          <button
            v-for="option in filterOptions"
            :key="option.value"
            class="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-md transition-colors"
            :class="
              installedFilter === option.value
                ? 'bg-white dark:bg-dark-4 shadow-sm text-light-text dark:text-dark-text'
                : 'text-light-disabled-text dark:text-dark-disabled-text'
            "
            @click="installedFilter = option.value"
          >
            <em
              v-if="option.icon"
              :class="['mdi', option.icon, option.iconClass]"
            />
            {{ option.label }}
          </button>
        </div>

        <div
          v-if="mergedDevices.length > 0"
          class="inline-flex rounded-lg bg-light-2 dark:bg-dark-2 p-0.5"
        >
          <button
            v-for="option in statusFilterOptions"
            :key="option.value"
            class="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-md transition-colors"
            :class="
              statusFilter === option.value
                ? 'bg-white dark:bg-dark-4 shadow-sm text-light-text dark:text-dark-text'
                : 'text-light-disabled-text dark:text-dark-disabled-text'
            "
            @click="statusFilter = option.value"
          >
            <em
              v-for="icon in option.icons"
              :key="icon.icon"
              :class="['mdi', icon.icon, icon.iconClass]"
            />
            {{ option.label }}
          </button>
        </div>

        <div
          class="ml-auto flex items-center gap-3 text-xs text-light-disabled-text dark:text-dark-disabled-text"
        >
          <span v-if="mergedDevices.length > 0">
            {{ sortedDevices.length }} Geräte
            <span v-if="backendEnabled && backendLoading">· Backend lädt…</span>
          </span>
        </div>
      </div>

      <table
        v-if="mergedDevices.length > 0"
        class="w-full text-sm bg-white dark:bg-transparent rounded-lg"
      >
        <thead>
          <tr
            class="text-left text-light-disabled-text dark:text-dark-disabled-text"
          >
            <th class="pb-2 font-medium">Seriennummer</th>
            <th class="pb-2 font-medium">Typ</th>
            <th
              class="pb-2 font-medium cursor-pointer select-none"
              @click="toggleSort('signal')"
            >
              Signal
              <em
                v-if="sort.column === 'signal' && sort.direction === 'asc'"
                class="mdi mdi-arrow-up text-xs"
              />
              <em
                v-else-if="
                  sort.column === 'signal' && sort.direction === 'desc'
                "
                class="mdi mdi-arrow-down text-xs"
              />
            </th>
            <th class="pb-2 font-medium">Status</th>
            <th
              class="pb-2 font-medium cursor-pointer select-none"
              @click="toggleSort('telegram')"
            >
              Letztes Telegram
              <em
                v-if="sort.column === 'telegram' && sort.direction === 'asc'"
                class="mdi mdi-arrow-up text-xs"
              />
              <em
                v-else-if="
                  sort.column === 'telegram' && sort.direction === 'desc'
                "
                class="mdi mdi-arrow-down text-xs"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="device in sortedDevices"
            :key="device.serialNumber"
            class="border-t border-light-3 dark:border-dark-3"
          >
            <td class="py-1.5">
              <span class="inline-flex items-center gap-1">
                <em
                  v-if="device.isInstalled"
                  class="mdi mdi-wrench text-sm text-primary-2"
                  title="Installiert"
                />
                {{ device.serialNumber }}
              </span>
            </td>
            <td class="py-1.5">{{ deviceTypeLabel(device.type) }}</td>
            <td class="py-1.5">
              <div v-if="device.rssi !== null" class="flex items-center gap-2">
                <DmgwSignalBars :rssi="device.rssi" />
                <span
                  class="text-xs text-light-disabled-text dark:text-dark-disabled-text"
                  >{{ device.rssi }} dBm</span
                >
              </div>
              <span v-else>—</span>
            </td>
            <td class="py-1.5">
              <span class="inline-flex items-center gap-0.5">
                <em
                  v-if="device.sources.has('gateway')"
                  class="mdi mdi-check-circle text-base text-green-500"
                  title="Gateway empfangen"
                />
                <em
                  v-if="device.sources.has('direct')"
                  class="mdi mdi-check-circle text-base text-yellow-400"
                  title="QTool empfangen"
                />
                <em
                  v-if="device.sources.has('backend')"
                  class="mdi mdi-check-circle text-base text-blue-500"
                  title="Backend empfangen"
                />
              </span>
            </td>
            <td
              class="py-1.5 text-[10px] text-light-disabled-text dark:text-dark-disabled-text"
            >
              {{ device.timestamp ? formatRelative(device.timestamp) : '—' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-else-if="
        (readoutState.status === 'fetching-key' ||
          readoutState.status === 'connecting') &&
        !hasPreviousResults
      "
    >
      <div class="flex items-center gap-2 text-sm">
        <em
          v-if="isConnected"
          class="mdi mdi-loading mdi-spin text-primary-2"
        />
        <em v-else class="mdi mdi-circle text-base text-gray-400" />
        <span v-if="readoutState.status === 'fetching-key'">
          AES-Schlüssel wird abgerufen…
        </span>
        <span v-else> Verbindung zum Gateway wird aufgebaut… </span>
      </div>
    </div>

    <div v-else-if="readoutState.status === 'error'">
      <BaseInformation
        :status="Status.ERROR"
        :information-text="readoutState.message"
      />
      <div class="flex gap-3 mt-4">
        <PrimaryButton label="Erneut versuchen" @click="$emit('retry')" />
        <SecondaryButton
          label="Zurück zur Übersicht"
          @click="$emit('cancel')"
        />
      </div>
    </div>
  </BaseSection>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import type {
  DeviceReadoutState,
  GatewayReadoutState,
} from '@/composables/useQToolOperations';
import { getDmgw6DeviceScan, type ScannedDevice } from '@/api/dmgw6/dmgw6Api';
import { useDeviceStore } from '@/store/entities/deviceStore';
import BaseSection from '@/components/UI/Card/BaseSection.vue';
import BaseInformation from '@/components/UI/Hint/BaseInformation.vue';
import PrimaryButton from '@/components/UI/Button/PrimaryButton.vue';
import SecondaryButton from '@/components/UI/Button/SecondaryButton.vue';
import DmgwSignalBars from './DmgwSignalBars.vue';
import DmgwReadoutDebug from './DmgwReadoutDebug.vue';
import { Status } from '@/enums/generic';
import { deviceTypesDE } from '@/enums/device';
import { timeAgo } from '@/helpers/timeAgo';

const props = defineProps<{
  readoutState: GatewayReadoutState;
  deviceReadoutState: DeviceReadoutState;
  isConnected: boolean;
}>();

defineEmits<{
  retry: [];
  cancel: [];
}>();

const gatewaySerial = computed(() =>
  'gateway' in props.readoutState ? props.readoutState.gateway.serialNumber : ''
);

const hasPreviousResults = computed(() => {
  const s = props.readoutState;
  return (
    (s.status === 'fetching-key' || s.status === 'connecting') &&
    'devices' in s &&
    !!s.devices &&
    s.devices.length > 0
  );
});

const nowTick = ref(Date.now());
let nowTickTimer: ReturnType<typeof setInterval> | null = null;
onMounted(() => {
  nowTickTimer = setInterval(() => {
    nowTick.value = Date.now();
  }, 1000);
});

function formatRelative(ts: number): string {
  void nowTick.value;
  return timeAgo(new Date(ts));
}

function deviceTypeLabel(type: string): string {
  return deviceTypesDE[type] ?? type;
}

const backendEnabled = ref(false);

const POLL_INTERVAL_MS = 5000;
const backendDevices = ref<ScannedDevice[]>([]);
const backendLoading = ref(false);
let pollTimer: ReturnType<typeof setInterval> | null = null;

async function fetchBackendDevices() {
  if (!gatewaySerial.value) return;
  backendLoading.value = true;
  try {
    const scan = await getDmgw6DeviceScan(gatewaySerial.value);
    backendDevices.value = scan.devices;
  } catch {
    /* noop */
  } finally {
    backendLoading.value = false;
  }
}

function startPolling() {
  stopPolling();
  fetchBackendDevices();
  pollTimer = setInterval(fetchBackendDevices, POLL_INTERVAL_MS);
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

onUnmounted(() => {
  stopPolling();
  if (nowTickTimer) {
    clearInterval(nowTickTimer);
    nowTickTimer = null;
  }
});

const installedSerials = computed(() => {
  const serials = new Set<string>();
  for (const device of useDeviceStore().getDevices().values()) {
    if (device.serialNumber) serials.add(device.serialNumber);
  }
  return serials;
});

type DeviceSource = 'gateway' | 'direct' | 'backend';

type InstalledFilter = 'all' | 'installed';
const installedFilter = ref<InstalledFilter>('installed');
const filterOptions: {
  value: InstalledFilter;
  label: string;
  icon?: string;
  iconClass?: string;
}[] = [
  { value: 'all', label: 'Alle' },
  {
    value: 'installed',
    label: 'Installiert',
    icon: 'mdi-wrench',
    iconClass: 'text-primary-2',
  },
];

type StatusFilter = 'all' | 'gateway' | 'qtool' | 'backend' | 'both';
const statusFilter = ref<StatusFilter>('all');
const GATEWAY_ICON = { icon: 'mdi-check-circle', iconClass: 'text-green-500' };
const DIRECT_ICON = { icon: 'mdi-check-circle', iconClass: 'text-yellow-400' };
const BACKEND_ICON = { icon: 'mdi-check-circle', iconClass: 'text-blue-500' };
type StatusFilterOption = {
  value: StatusFilter;
  label: string;
  icons?: { icon: string; iconClass: string }[];
};
const allStatusFilterOptions: StatusFilterOption[] = [
  { value: 'all', label: 'Alle' },
  { value: 'gateway', label: 'Gateway', icons: [GATEWAY_ICON] },
  { value: 'qtool', label: 'QTool', icons: [DIRECT_ICON] },
  { value: 'backend', label: 'Backend', icons: [BACKEND_ICON] },
  { value: 'both', label: 'Beide', icons: [GATEWAY_ICON, DIRECT_ICON] },
];
const statusFilterOptions = computed(() =>
  backendEnabled.value
    ? allStatusFilterOptions
    : allStatusFilterOptions.filter((o) => o.value !== 'backend')
);

watch(
  [() => props.readoutState.status, backendEnabled],
  ([status, enabled]) => {
    if (enabled && (status === 'reading' || status === 'done')) {
      startPolling();
    } else {
      stopPolling();
      if (!enabled) {
        backendDevices.value = [];
        if (statusFilter.value === 'backend') statusFilter.value = 'all';
      }
    }
  },
  { immediate: true }
);

type MergedDevice = {
  serialNumber: string;
  type: string;
  rssi: number | null;
  sources: Set<DeviceSource>;
  isInstalled: boolean;
  timestamp: number | null;
};

const mergedDevices = computed<MergedDevice[]>(() => {
  const gwState = props.readoutState;
  const gwDevices = 'devices' in gwState ? gwState.devices : [];
  const directState = props.deviceReadoutState;
  const directDevices = 'devices' in directState ? directState.devices : [];

  const map = new Map<string, MergedDevice>();

  function upsert(
    serial: string,
    type: string,
    rssi: number | null,
    source: DeviceSource,
    ts: number | null = null
  ) {
    const existing = map.get(serial);
    if (existing) {
      existing.sources.add(source);
      existing.rssi = rssi ?? existing.rssi;
      existing.type = type || existing.type;
      if (ts != null) existing.timestamp = ts;
    } else {
      map.set(serial, {
        serialNumber: serial,
        type,
        rssi,
        sources: new Set([source]),
        isInstalled: installedSerials.value.has(serial),
        timestamp: ts,
      });
    }
  }

  for (const d of backendDevices.value) {
    upsert(d.serialNumber, d.manufacturer ?? '', d.rssi, 'backend');
  }
  for (const d of directDevices) {
    upsert(d.serialNumber, d.type, d.rssi, 'direct', d.timestamp);
  }
  for (const d of gwDevices) {
    upsert(d.serialNumber, d.type, d.rssi, 'gateway', d.timestamp);
  }

  return [...map.values()];
});

const filteredDevices = computed(() => {
  let list = mergedDevices.value;
  if (installedFilter.value === 'installed') {
    list = list.filter((d) => d.isInstalled);
  }
  if (statusFilter.value === 'gateway') {
    list = list.filter((d) => d.sources.has('gateway'));
  } else if (statusFilter.value === 'qtool') {
    list = list.filter((d) => d.sources.has('direct'));
  } else if (statusFilter.value === 'backend') {
    list = list.filter((d) => d.sources.has('backend'));
  } else if (statusFilter.value === 'both') {
    list = list.filter(
      (d) => d.sources.has('gateway') && d.sources.has('direct')
    );
  }
  return list;
});

type SortColumn = 'signal' | 'telegram';
type SortDirection = 'asc' | 'desc';
type SortState = { column: SortColumn | null; direction: SortDirection };
const sort = ref<SortState>({ column: null, direction: 'desc' });

function toggleSort(column: SortColumn) {
  if (sort.value.column !== column) {
    sort.value = { column, direction: 'desc' };
  } else if (sort.value.direction === 'desc') {
    sort.value = { column, direction: 'asc' };
  } else {
    sort.value = { column: null, direction: 'desc' };
  }
}

const sortedDevices = computed(() => {
  if (sort.value.column === null) return filteredDevices.value;
  const dir = sort.value.direction === 'desc' ? -1 : 1;
  const column = sort.value.column;
  return [...filteredDevices.value].sort((a, b) => {
    let aVal: number;
    let bVal: number;
    if (column === 'signal') {
      aVal = a.rssi ?? -Infinity;
      bVal = b.rssi ?? -Infinity;
    } else {
      aVal = a.timestamp ?? -Infinity;
      bVal = b.timestamp ?? -Infinity;
    }
    return (aVal - bVal) * dir;
  });
});
</script>
